import * as React from 'react';
import { cn } from '@lib/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'ghost' | 'link' | 'destructive';
  size?: 'fit' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props },
    ref,
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-[--radi-6] border border-[--base-border] font-semibold shadow-elevation-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants = {
      primary:
        'bg-primary text-[--text-light-primary] hover:bg-[--base-surface-1] hover:border-[--state-brand-active] hover:text-[--state-brand-active]',
      secondary:
        'bg-[--base-surface-2] text-[--text-dark-primary] [&>i]:text-[--base-fill-5] hover:bg-opacity-80',
      tertiary: 'bg-[--base-fill-1]',
      outline:
        'border border-input bg-background [&>i]:text-[--base-fill-5] hover:bg-accent hover:text-accent-foreground',
      ghost:
        'border-none shadow-none [&>i]:text-[--base-fill-4]  hover:bg-[--colors-alpha-dark-100] hover:text-[--text-dark-primary]',
      link: '!px-0 py-[--spacing-7] gap-[--spacing-5] border-none shadow-none text-[--state-brand-active] hover:underline',
      destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    };

    const sizes = {
      fit: 'h-fit text-sm [&>i]:text-base px-[--spacing-3] gap-[--spacing-3]',
      sm: 'h-[36px] text-sm [&>i]:text-base px-[--spacing-7] gap-[--spacing-3]',
      md: 'h-[44px] text-base [&>i]:text-xl px-[--spacing-8] gap-[--spacing-3]',
      lg: 'h-12 px-8',
    };

    return (
      <button
        type="button"
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
